import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import styles from "./Engagements.module.scss";
import TextButton from "../../../common/TextButton/TextButton";
import Modal from "../../../common/Modal/Modal";
import AddEngagementPopup from "./AddEngagementPopup/AddEngagementPopup";
import { useAppSelector } from "../../../../redux/hooks";
import EngagementTable from "./EngagementTable/EngagementTable";
import { UploadedFile } from "../../../common/UploadInput/UploadInput";
import {
  useCreateEngagementMutation,
  useDeleteEngagementMutation,
  useListAllEngagementQuery,
  useListEngagementByIdQuery,
  useUpdateEngagementMutation,
} from "../../../../redux/services/goalsAPI";
import { useProfileParams } from "../../../../utils/common";
import SuccessionDocLoader from "../../../common/Loaders/SuccessionDocLoader/SuccessionDocLoader";
import ErrorInPageScreen from "../../../common/ErrorInPageScreen/ErrorInPageScreen";
import { isArray, isEmpty, isError } from "lodash";
import { toastMessageType, engagementRespData } from "../../../../types/types";
import { isValidResponse } from "../../../../utils/validateAPIResponse";
import ToastMessage from "../../../common/ToastMessage/ToastMessage";

export type Engagement = {
  id: string;
  title: string;
  date: string | Date;
  type: string;
  description: string;
  channel?: string;
  attachments: UploadedFile[];
  linkedGoals: number[];
};

type EngagementProps = {
  goalID?: number | null;
  isHistoryGoalData?: boolean;
};

const Engagements = ({ goalID, isHistoryGoalData }: EngagementProps) => {
  const { t } = useTranslation();
  const { affiliateCode, aboNumber } = useProfileParams();
  const [showAddModal, setShowAddModal] = useState(false);
  const currentTheme: any = useAppSelector(
    (state: any) => state.theme.currentTheme
  );
  const [engagements, setEngagements] = useState<Engagement[]>([]);
  const [isAPIError, setIsAPIError] = useState<boolean>(false);
  const [toastConfig, setToastConfig] = useState({
    message: "",
    type: "success" as toastMessageType,
    show: false,
  });

  const [
    createEngagement,
    { isLoading: isCreateLoading, isError: isCreateError },
  ] = useCreateEngagementMutation();
  const [
    updateEngagement,
    { isLoading: isUpdateLoading, isError: isUpdateError },
  ] = useUpdateEngagementMutation();

  const {
    data: getListEngagementByIdData,
    isLoading: isListEngagementByIdLoading,
    isFetching: isListEngagementByIdFetching,
    isError: isListEngagementByIdError,
  } = useListEngagementByIdQuery(
    {
      aboNumber,
      affiliateCode,
      goalId: goalID,
    },
    {
      skip: !goalID,
    }
  );

  const {
    data: getListAllEngagementQueryData,
    isLoading: isListAllEngagementLoading,
    isFetching: isListAllEngagementFetching,
    isError: isListAllEngagementError,
  } = useListAllEngagementQuery(
    {
      aboNumber,
      affiliateCode,
    },
    {
      skip: !!goalID,
    }
  );

  useEffect(() => {
    const rawData = getListEngagementByIdData || getListAllEngagementQueryData;
    if (isArray(rawData?.body) && isEmpty(rawData?.error)) {
      const formattedData = rawData?.body?.map(
        (
          engagementData: engagementRespData & {
            aboGoalIds?: number[];
            aboGoalId?: number;
          }
        ) => ({
          id: engagementData.aboGoalEngagementId.toString(),
          title: engagementData.title,
          date: engagementData.engagementDate,
          type: engagementData.engagementType.toString(),
          description: engagementData.note,
          channel: engagementData.channelType.toString(),
          attachments: (engagementData.documents || []).map((doc: any) => ({
            id: doc.aboGoalEngagementDocumentId?.toString() || `${Date.now()}`,
            name: doc.name || "",
            size: doc.size,
            status: doc.status,
            dataUrl: doc.url || "#",
            type: doc.type || "file",
          })),
          // carry linked goals from API if present
          linkedGoals: Array.isArray(engagementData.aboGoalIds)
            ? engagementData.aboGoalIds
            : engagementData.aboGoalId
              ? [Number(engagementData.aboGoalId)]
              : [],
        })
      );
      setEngagements(formattedData || []);
    } else {
      setEngagements([]);
    }
  }, [getListEngagementByIdData, getListAllEngagementQueryData]);

  const [
    deleteEngagementById,
    {
      isLoading: isDeleteEngagementByIdLoading,
      isError: isDeleteEngagementByIdError,
    },
  ] = useDeleteEngagementMutation();

  const setToastMessage = (message: string, msgType: toastMessageType) => {
    setToastConfig({
      type: msgType,
      message: message,
      show: true,
    });
  };

  const handleClickAdd = () => {
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
  };

  const handleAPIError = () => {
    setIsAPIError(true);
    setEngagements([]);
  };

  useEffect(() => {
    if (
      isCreateError ||
      isUpdateError ||
      isListEngagementByIdError ||
      isListAllEngagementError
    )
      handleAPIError();
  }, [isError]);

  const handleAddEngagement = async (engagementData: any) => {
    const formData = new FormData();

    // aboGoalIds should be a comma-separated list of goal IDs
    const ids: number[] = Array.isArray(engagementData.aboGoalIds)
      ? engagementData.aboGoalIds
      : [];
    if (ids.length > 0) {
      formData.append("aboGoalIds", ids.join(","));
    }

    formData.append("title", engagementData.title);
    formData.append("note", engagementData.description || "");
    formData.append(
      "engagementType",
      String(engagementData?.type?.value ?? engagementData?.type)
    );
    formData.append(
      "channelType",
      String(engagementData?.channel?.value ?? engagementData?.channel)
    );
    formData.append("engagementDate", engagementData.date);

    (engagementData.attachments || []).forEach((file: UploadedFile) => {
      if (file.fileObject) {
        formData.append("file", file.fileObject);
      }
    });

    try {
      const response = await createEngagement({
        affiliateCode,
        aboNumber,
        payload: formData,
      }).unwrap();

      const body = response.body;

      const newEngagement: Engagement = {
        id: body.aboGoalEngagementId.toString(),
        title: body.title,
        date: body.engagementDate,
        type: body.engagementType.toString(),
        description: body.note,
        channel: body.channelType.toString(),
        attachments: (body.documents || []).map((doc: any) => ({
          id: doc.aboGoalEngagementDocumentId?.toString() || `${Date.now()}`,
          name: doc.name || "",
          size: doc.size,
          status: doc.status,
          dataUrl: doc.url || "#",
          type: doc.type || "file",
        })),
        // prefer API echo; fallback to submitted aboGoalIds
        linkedGoals: Array.isArray(body?.aboGoalIds)
          ? (body.aboGoalIds as number[])
          : Array.isArray(engagementData?.aboGoalIds)
            ? (engagementData.aboGoalIds as number[])
            : [],
      };
      // TODO: these amnual update should remove once API works fine
      setEngagements((prev) => [newEngagement, ...prev]);
      // setShowAddModal(false);
    } catch (err) {
      handleAPIError();
      console.error("Create engagement failed", err);
    }
  };

  const handleDelete = async (id: string) => {
    deleteEngagementById({ engagementId: id })
      .then((deletedResp: any) => {
        if (deletedResp?.data?.statusCode === 200) {
          setToastMessage("Engagement deleted successfully", "success");
        } else {
          setToastMessage(
            "Unable to delete engagement. Please try again.",
            "error"
          );
        }
      })
      .catch((err) => {
        setToastMessage(
          "Unable to delete engagement. Please try again.",
          "error"
        );
        console.error("Delete engagement failed", err);
      });
  };

  const handleEdit = async (engagementData: any) => {
    const formData = new FormData();
    formData.append("aboGoalEngagementId", engagementData.id);

    // aboGoalIds should be a comma-separated list of goal IDs
    const ids: number[] = Array.isArray(engagementData.aboGoalIds)
      ? engagementData.aboGoalIds
      : [];
    if (ids.length > 0) {
      formData.append("aboGoalIds", ids.join(","));
    }

    formData.append("title", engagementData.title);
    formData.append("note", engagementData.description || "");
    formData.append(
      "engagementType",
      String(engagementData?.type?.value ?? engagementData?.type)
    );
    formData.append(
      "channelType",
      String(engagementData?.channel?.value ?? engagementData?.channel)
    );
    formData.append("engagementDate", engagementData.date);

    // Append all files from engagement state (old + new)
    engagementData.attachments.forEach((file: UploadedFile) => {
      if (file.fileObject) {
        // New file with binary data
        formData.append("file", file.fileObject, file.name);
      } else if (file.name) {
        // Existing file - send only the name so backend recognizes it
        // Create a minimal blob with just the name to satisfy FormData API
        const nameBlob = new Blob([file.name], { type: "text/plain" });
        formData.append("file", nameBlob, file.name);
      }
    });

    try {
      const response = await updateEngagement({
        affiliateCode,
        aboNumber,
        payload: formData,
      }).unwrap();

      const body = response.body;

      const updatedEngagementFromAPI: Engagement = {
        id: body.aboGoalEngagementId.toString(),
        title: body.title,
        date: body.engagementDate,
        type: body.engagementType.toString(),
        description: body.note,
        channel: body.channelType.toString(),
        attachments: (body.documents || []).map((doc: any) => ({
          id: doc.aboGoalEngagementDocumentId?.toString() || `${Date.now()}`,
          name: doc.name || "",
          size: doc.size,
          status: doc.status,
          dataUrl: doc.url || "#",
          type: doc.type || "file",
          isExisting: true,
        })),
        linkedGoals: Array.isArray(body?.aboGoalIds)
          ? (body.aboGoalIds as number[])
          : Array.isArray(engagementData?.aboGoalIds)
            ? (engagementData.aboGoalIds as number[])
            : [],
      };

      setEngagements((prev) =>
        prev.map((e) =>
          e.id === engagementData.id ? { ...e, ...updatedEngagementFromAPI } : e
        )
      );
      setShowAddModal(false);
    } catch (err) {
      handleAPIError();
      console.error("Update engagement failed", err);
    }
  };

  const hasEngagements = engagements.length > 0;

  return (
    <div className={styles.engagements_wrapper}>
      {/* Heading with edit icon */}
      <div className={styles.heading_area}>
        <span
          className={styles.heading}
          data-testid="engagements_bg_heading_testID"
        >
          {t("engagements")}
        </span>
        {!isHistoryGoalData && (
          <span
            className={styles.right_section}
            data-testid="add_engagement_testID"
          >
            <TextButton
              label={t("addEngagement")}
              onClick={handleClickAdd}
              data-testid="edit_testID"
              isIcon={true}
              iconName="plus_big"
              iconSize={16}
              themeColor={currentTheme === "light" ? "#000" : "#fff"}
            />
          </span>
        )}
      </div>

      {/* Engagements Section */}
      {isCreateLoading ||
      isUpdateLoading ||
      isListEngagementByIdLoading ||
      isListAllEngagementLoading ||
      isDeleteEngagementByIdLoading ||
      isListAllEngagementFetching ||
      isListEngagementByIdFetching ? (
        <SuccessionDocLoader num={1} heading={t("engagements")} />
      ) : isAPIError ? (
        <>
          <ErrorInPageScreen
            handleClickTryAgain={() => {
              setIsAPIError(false);
            }}
          />
        </>
      ) : hasEngagements ? (
        <div className={styles.engagements_table}>
          <EngagementTable
            data={engagements}
            rowLimit={3}
            onDelete={handleDelete}
            onEdit={handleEdit}
            isHistoryGoalData={isHistoryGoalData}
          />
        </div>
      ) : (
        <div className={styles.engagements_section}>
          <div className={styles.empty_engagements}>
            {t("noEngagementsMsg")}
          </div>
        </div>
      )}

      {/* Add Engagement Modal */}
      <Modal showDrawer={showAddModal} onClose={handleCloseModal}>
        <AddEngagementPopup
          goalID={goalID}
          onSave={handleAddEngagement}
          onCancel={handleCloseModal}
        />
      </Modal>
      {/* Toast notification */}
      <ToastMessage
        message={toastConfig.message}
        toastType={toastConfig.type}
        showToast={toastConfig.show}
        setShowToast={(show) => setToastConfig((prev) => ({ ...prev, show }))}
        customStyles={{
          top: "96px",
          left: "50%",
          transform: "translateX(-50%)",
        }}
        autoDismissal
      />
    </div>
  );
};

export default Engagements;
